/*eslint react-hooks/exhaustive-deps: "off"*/
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import L from "leaflet";
import {
  Map as LeafletMap,
  TileLayer,
  CircleMarker,
  Marker,
  LayerGroup,
  WMSTileLayer,
  GeoJSON,
  Polyline,
  Tooltip,
} from "react-leaflet";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";

import Axios from "axios";
import {
  faBacon,
  faFlag,
  faPlusSquare,
  faRoad,
  faTree,
} from "@fortawesome/free-solid-svg-icons";

import {
  GOOGLE_MAPS_API_KEY,
  VWORLD_APIKEY,
  MAP_VENDOR,
  GOOGLE_MAPS_VERSION,
  GOOGLE_MAPS_REGION,
} from "../../../Constants";
import { arrayToRgb, getApiHost, makeQueryString, zip } from "../../../utils";
import { GEOSERVER_URL, JSON_SIGUNGU } from "../../../Constants";
import HeatmapLayer from "./HeatmapLayer";
import MarkerClusterGroup from "./MarkerClusterGroup";
import MapPropsContext from "../MapPropsContext";
import { correctFeatureProperties } from "../useMapProps";

import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster/dist/MarkerCluster.css";

import "./MapBaseLeaflet.scss";

// 와이파이 분포 레이어를 추가합니다. WIFI_ZONE_SHP
// 5g 분포 레이어를 추가합니다.
// SK_UP
// SK_DN
// KT_UP
// KT_DN
// LG_UP
// LG_DN
export const LAYER_WMS_TYPES = {
  KCA_group_uq: {
    layerName: "KCA:group_uq",
    toolTip: "용도지역",
    fontAwesomeIcon: faBacon,
    // iconSrc: action_용도지역,
  },
  PMS_SK: {
    layerName: "PMS_SK",
    toolTip: "이동통신 무선국 분포현황 : SK",
    fontAwesomeIcon: faFlag,
  },
  PMS_KT: {
    layerName: "PMS_KT",
    toolTip: "이동통신 무선국 분포현황 : KT",
    fontAwesomeIcon: faFlag,
  },
  PMS_LG: {
    layerName: "PMS_LG",
    toolTip: "이동통신 무선국 분포현황 : LG",
    fontAwesomeIcon: faFlag,
  },
  PMS_2G: {
    layerName: "PMS_2G",
    toolTip: "이동통신 무선국 분포현황 : 2G",
    fontAwesomeIcon: faPlusSquare,
  },
  PMS_3G: {
    layerName: "PMS_3G",
    toolTip: "이동통신 무선국 분포현황 : 3G",
    fontAwesomeIcon: faPlusSquare,
  },
  PMS_4G: {
    layerName: "PMS_4G",
    toolTip: "이동통신 무선국 분포현황 : 4G",
    fontAwesomeIcon: faPlusSquare,
  },
  WIFI_ZONE_SHP: {
    layerName: "WIFI_ZONE_SHP",
    toolTip: "와이파이 분포현황",
    fontAwesomeIcon: faPlusSquare,
  },
  PARK_AREA_SHP: {
    layerName: "PARK_AREA_SHP",
    toolTip: "국립공원",
    fontAwesomeIcon: faTree,
  },
  GSTN_PMNTN_LN: {
    layerName: "GSTN_PMNTN_LN",
    toolTip: "산악지역 / 탐방로",
    fontAwesomeIcon: faRoad,
  },
  FIVEG_ALL: {
    layerName: "5G",
    toolTip: "5G 무선국 분포현황",
    fontAwesomeIcon: faPlusSquare,
  },
  SK_UP: {
    layerName: "SK_UP",
    toolTip: "5G 무선국 분포현황 : SK(지상)",
    fontAwesomeIcon: faPlusSquare,
  },
  SK_DN: {
    layerName: "SK_DN",
    toolTip: "5G 무선국 분포현황 : SK(지하)",
    fontAwesomeIcon: faPlusSquare,
  },
  KT_UP: {
    layerName: "KT_UP",
    toolTip: "5G 무선국 분포현황 : KT(지상)",
    fontAwesomeIcon: faPlusSquare,
  },
  KT_DN: {
    layerName: "KT_DN",
    toolTip: "5G 무선국 분포현황 : KT(지하)",
    fontAwesomeIcon: faPlusSquare,
  },
  LG_UP: {
    layerName: "LG_UP",
    toolTip: "5G 무선국 분포현황 : LG(지상)",
    fontAwesomeIcon: faPlusSquare,
  },
  LG_DN: {
    layerName: "LG_DN",
    toolTip: "5G 무선국 분포현황 : LG(지하)",
    fontAwesomeIcon: faPlusSquare,
  },
};

const MapBaseLeaflet = ({
  children,
  containerOffsetTop,
  mapContainerStyle,
}) => {
  const {
    viewState,
    mapBounds,
    setMapBounds,
    mapViewType,
    mapType,
    mapLayerNames,
    visibleRadius,
    setMapCenter,
    setZoom,
    manualDistrictLayerParamsByGeoserver,
    geojsonPolygonLayerProps,
    geojsonPolygonsResponseList,
    setClickedPolygonGeojsonObjects,
    isClusteringPoint,
    isHeapmapByPoint,
    pointLayerProps,
    filteredPointsResponseList,
    isHideFilteredPointsLayer,
    isEnableDistanceToClickedPoint,
    clickedPointIconObjects,
    setClickedPointIconObjects,
    mountainReceiveSensitivityLayerList,
    mountainExtraLayerDict,
    iconDict,
    mapDistrictStats,
    widgetState,
    currentDistrictCode,
    notAllAreaPolygon,
    isNationalParkHeapmap,
  } = useContext(MapPropsContext);

  const leafletMapRef = useRef();

  const { latitude, longitude, zoom } = viewState;
  const [circleCenter, setCircleCenter] = useState({
    latitude,
    longitude,
    zoom,
  });

  const [manualGeojsonDataByGeoserver, setManualGeojsonDataByGeoserver] =
    useState();
  const [forceUpdateManualGeojsonLayer, setForceUpdateManualGeojsonLayer] =
    useState(false);

  const [districtLv2List, setDistrictLv2List] = useState([]);

  useEffect(() => {
    let oldOffsetWidth = null;
    const refreshIfChangedWidth = () => {
      const leafletMap = leafletMapRef.current;
      const offsetWidth = leafletMap.container.offsetWidth;
      if (oldOffsetWidth !== offsetWidth) {
        leafletMap.leafletElement.invalidateSize();
        oldOffsetWidth = offsetWidth;
      }
    };
    const id = setInterval(refreshIfChangedWidth, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const { typeName, CQL_FILTER } = manualDistrictLayerParamsByGeoserver;

    setManualGeojsonDataByGeoserver(null);

    if (typeName) {
      const url = GEOSERVER_URL + "KCA/ows";
      const params = {
        service: "WFS",
        request: "GetFeature",
        typeName,
        outputFormat: "application/json",
      };

      if (!CQL_FILTER && notAllAreaPolygon) return;

      if (CQL_FILTER) params["CQL_FILTER"] = CQL_FILTER;

      const queryString = makeQueryString(params);
      const requestUrl = url + "?" + queryString;

      const proxyUrl = getApiHost() + "/gis/proxy.do";
//      Axios.get(proxyUrl, { params: { url: requestUrl } })
      Axios.get(requestUrl)
        .then(({ data }) => {
          if (typeof data === "object" && data.type) {
            setManualGeojsonDataByGeoserver(data);
          } else {
            console.group("Invalid GeoJSON response");
            console.error(data);
            console.groupEnd();
          }
        })
        .catch((reason) => console.error(reason));
    }
  }, [manualDistrictLayerParamsByGeoserver]);

  const vworldTileUrl =
    MAP_VENDOR === "vworld"
      ? mapType === "leaflet-vworld-base"
        ? `//api.vworld.kr/req/wmts/1.0.0/${VWORLD_APIKEY}/Base/{z}/{y}/{x}.png`
        : `https://xdworld.vworld.kr/2d/Satellite/service/{z}/{x}/{y}.jpeg`
      : null;

  let googleMapType =
    MAP_VENDOR === "google"
      ? mapType === "leaflet-google-base"
        ? "roadmap"
        : "hybrid"
      : null;

  const metersPerPixel = useMemo(() => {
    const { latitude, zoom } = circleCenter;
    return (
      (40075016.686 * Math.abs(Math.cos((latitude * Math.PI) / 180))) /
      Math.pow(2, zoom + 8)
    );
  }, [circleCenter, zoom]);

  // Allows vector layers to be displayed with <canvas>
  const renderer = useMemo(() => L.canvas(), []);

  const onClickedPoint = useCallback(
    (e) => {
      const {
        layerPoint: { x, y },
      } = e;

      const closedPoints = filteredPointsResponseList
        .map((pointsList) => {
          return pointsList.filter(({ position: [pointLng, pointLat] }) => {
            const point =
              leafletMapRef.current.leafletElement.latLngToLayerPoint([
                pointLat,
                pointLng,
              ]);
            const dx = x - point.x;
            const dy = y - point.y;
            const distance_pixels = Math.sqrt(dx * dx + dy * dy);

            // 인접 포인트 노출시 픽셀 인접계산 수식 조정
            let standardValue = 20;

            if (zoom >= 16) {
              return distance_pixels <= standardValue;
            } else if (zoom >= 15) {
              return distance_pixels <= standardValue / 2;
            } else if (zoom >= 14) {
              return distance_pixels <= standardValue / 4;
            } else if (zoom >= 13) {
              return distance_pixels <= standardValue / 10;
            } else if (zoom >= 12) {
              return distance_pixels <= standardValue / 20;
            } else {
              return distance_pixels <= standardValue;
            }
            // return distance_pixels <= 1;  // 30픽셀 근접 아이콘만 필터링 -> 20.1.9(목) 요청사항 : 근접 아이콘 필터링 되는 값을 확 줄여달라 해서 수정함
          });
        })
        .flat();

      setClickedPointIconObjects(closedPoints);
    },
    [filteredPointsResponseList, setClickedPointIconObjects, zoom]
  );

  //
  // geojson 레이어 스타일링
  //
  const getStyleForFeature = useCallback(
    (feature) => {
      const array = geojsonPolygonLayerProps.getFillColor(feature);
      if (!array) return {};
      const fillOpacity255 = array[3] || 255;
      const fillOpacity = fillOpacity255 / 255;
      const fillColor = arrayToRgb(array);
      const borderProps =
        fillOpacity255 <= 1
          ? { weight: 0 }
          : { weight: 2, dashArray: "3", color: "white" };
      return {
        fillColor,
        fillOpacity,
        ...borderProps,
      };
    },
    [geojsonPolygonLayerProps]
  );

  const highlightFeature = useCallback(({ target }) => {
    const layer = target;

    layer.setStyle({
      weight: 2,
      color: "#0000CD",
      dashArray: "",
      fillOpacity: 0,
      fontSize: 15,
    });

    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      layer.bringToFront();
    }
  }, []);

  const resetHighlight = useCallback(
    ({ target }) => {
      const { feature } = target; // target is layer.
      target.setStyle(getStyleForFeature(feature));
    },
    [getStyleForFeature]
  );

  useEffect(() => {
    // 시군구 단위의 district list를 가져옴
    if (currentDistrictCode && currentDistrictCode.length === 2) {
      /*
      const url = getApiHost() + '/address/getSigugun.json';
      const params = {ctprvn_cd: currentDistrictCode};
      Axios.get(url, {params})
      .then(({data}) => {
        setDistrictLv2List(data);
      });
       */
      setDistrictLv2List(
        JSON_SIGUNGU.filter(({ SID_CD }) => SID_CD === currentDistrictCode)
      );
    }
  }, [currentDistrictCode]);

  const onEachFeature = useCallback(
    (feature, layer) => {
      layer.on({
        mouseover: highlightFeature,
        mouseout: resetHighlight,
        click: ({ target: { feature } }) => {
          const newFeature = correctFeatureProperties(feature);
          setClickedPolygonGeojsonObjects([newFeature]);

          const {
            properties: { pk },
          } = newFeature;

          // 시군구 단위 폴리곤 클릭 시 setMapBounds 설정함
          if (pk && pk.length > 2) {
            const row = districtLv2List.find(({ SIG_CD }) => SIG_CD === pk);
            if (row) {
              const { LX, LY, RX, RY } = row;
              setMapBounds([
                [LY, LX],
                [RY, RX],
              ]);
            }
          }
        },
      });
      const { getHoverHtml } = geojsonPolygonLayerProps;

      if (getHoverHtml) {
        layer.bindTooltip((layer) => getHoverHtml(layer.feature), {
          direction: "auto",
          permanent: false,
          sticky: true,
        });
        // .on("click", function () {
        //   tileEvent.tile.setAttribute("alt", "Map tile image");
        // });
      } else {
        layer.bindTooltip(
          (layer) => {
            const {
              feature: {
                properties: { SID_KOR_NM, SIG_KOR_NM, name },
              },
            } = layer;
            if (SID_KOR_NM) return SID_KOR_NM;
            if (SIG_KOR_NM) return SIG_KOR_NM;
            return name;
          },
          {
            direction: "auto",
            permanent: false,
            sticky: true,
          }
        );
      }
    },
    [
      mapDistrictStats,
      geojsonPolygonLayerProps,
      setClickedPolygonGeojsonObjects,
      highlightFeature,
      resetHighlight,
      districtLv2List,
      setMapBounds,
    ]
  );

  useEffect(() => {
    setForceUpdateManualGeojsonLayer(true);
    setTimeout(() => {
      setForceUpdateManualGeojsonLayer(false);
    }, 400);
  }, [mapDistrictStats]);

  useEffect(() => {
    setCircleCenter({ latitude, longitude, zoom });
  }, [latitude, longitude, zoom]);

  useEffect(() => {
    if (!mapViewType) {
      // latitude/longitude/zoom과 widgetState 변경 시에 클릭 정보 초기화
      setClickedPointIconObjects((prev) => {
        // if ( prev.length === 0 ) {
        //   return prev;
        // }

        const firstPoint = prev[0];
        if (firstPoint) {
          // 허가번호 검색 시에는 클릭 정보를 초기화하지 않습니다.
          if (
            firstPoint.latitude === latitude &&
            firstPoint.longitude === longitude
          ) {
            return prev;
          }
        }
        return [];
      });
    }
  }, [latitude, longitude, zoom, widgetState]);

  //
  // rendering markers
  //

  // TODO: 개선하기
  // 일반 무선국 마커
  const markersByPointsList = useMemo(() => {
    return filteredPointsResponseList.map((pointsList) => {
      const isClickedPointIconObjects = clickedPointIconObjects.length > 0;

      const uniquePostsList = Object.values(
        pointsList.reduce((unique, point) => {
          const { position } = point;
          if (unique[position] && pointLayerProps.isBigIconCode) {
            if (pointLayerProps.isBigIconCode(unique[position], point)) {
              unique[position] = point;
            }
          } else {
            unique[position] = point;
          }
          return unique;
        }, {})
      );

      return uniquePostsList.map((feature, markerIndex) => {
        const {
          position: [longitude, latitude],
        } = feature;
        const iconCode = pointLayerProps.getIconCode(feature, zoom);
        if (!iconCode) return null;
        const iconWidth = pointLayerProps.getIconWidth(feature, zoom);
        const { data, width, height, anchorX, anchorY } = iconDict[iconCode];
        const ratio = iconWidth / width;
        const iconSize = [iconWidth, Math.floor(height * ratio)];
        const iconAnchor = [anchorX * ratio, anchorY * ratio];
        const icon = L.icon({ iconUrl: data, iconSize, iconAnchor });
        return (
          <Marker
            keyboard={false}
            position={[latitude, longitude]}
            icon={icon}
            onClick={onClickedPoint}
            opacity={isClickedPointIconObjects ? 0.3 : 1}
            key={markerIndex}
          />
        );
      });
    });
  }, [filteredPointsResponseList, clickedPointIconObjects]);

  // setMapBounds를 통한 지도 위치 변경에서는 viewState.zoom 속성값이 자동적용되지 않기에, 로직으로 적용해줍니다.
  useEffect(() => {
    if (leafletMapRef.current) {
      const realZoom = leafletMapRef.current.leafletElement.getZoom();
      if (zoom !== realZoom) {
        setZoom({ zoom: realZoom });
      }
    }
  }, [setZoom, mapBounds]);

  //
  // 컴포넌트 렌더링
  //

  const { height } = mapContainerStyle || {};

  return (
    <div
      style={{
        ...DEFAULT_STYLE,
        ...mapContainerStyle,
        height: `calc(100vh - ${containerOffsetTop}px)`,
      }}
      className={"map-base-container-r"}
    >
      <LeafletMap
        ref={leafletMapRef}
        renderer={renderer}
        center={[latitude, longitude]}
        bounds={mapBounds}
        zoom={zoom}
        minZoom={7}
        maxZoom={viewState.maxZoom} // supported max zoom : 19
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={"center"}
        onViewportChange={({ center: [latitude, longitude], zoom }) => {
          setCircleCenter({ latitude, longitude, zoom });
        }}
        onViewportChanged={({ center: [latitude, longitude], zoom }) => {
          setMapCenter({ latitude, longitude, zoom });
        }}
        style={{ height: height || `calc(100vh - ${containerOffsetTop}px)` }}
      >
        {vworldTileUrl && <TileLayer url={vworldTileUrl} />}

        {googleMapType && (
          <ReactLeafletGoogleLayer
            googleMapsLoaderConf={{
              KEY: GOOGLE_MAPS_API_KEY,
              VERSION: GOOGLE_MAPS_VERSION,
              REGION: GOOGLE_MAPS_REGION,
              // LIBRARIES: ['geometry', 'visualization'],
            }}
            type={googleMapType}
            key={googleMapType}
          />
        )}

        {!forceUpdateManualGeojsonLayer && manualGeojsonDataByGeoserver && (
          <GeoJSON
            data={manualGeojsonDataByGeoserver}
            style={getStyleForFeature}
            onEachFeature={onEachFeature}
          />
        )}

        {!forceUpdateManualGeojsonLayer &&
          !manualGeojsonDataByGeoserver &&
          geojsonPolygonsResponseList.map((geojsonFeature, index) => (
            <GeoJSON
              key={index}
              data={geojsonFeature}
              style={getStyleForFeature}
              onEachFeature={onEachFeature}
            />
          ))}

        {mapLayerNames.map((layerName) => (
          <WMSTileLayer
            key={layerName}
            layers={layerName}
            url={"https://spectrummap.kr/geoserver/KCA/wms"}
            format={"image/png"}
            transparent={true}
            version={"1.1.0"}
          />
        ))}

        {!mapViewType && (
          <>
            {isHeapmapByPoint &&
              pointLayerProps.latitudeExtractorForHeatmap &&
              pointLayerProps.longitudeExtractorForHeatmap &&
              pointLayerProps.intensityExtractorForHeatmap &&
              filteredPointsResponseList.map((pointsList, index) => (
                <HeatmapLayer
                  points={pointsList}
                  key={index}
                  latitudeExtractor={
                    pointLayerProps.latitudeExtractorForHeatmap
                  }
                  longitudeExtractor={
                    pointLayerProps.longitudeExtractorForHeatmap
                  }
                  intensityExtractor={
                    pointLayerProps.intensityExtractorForHeatmap
                  }
                />
              ))}

            {isNationalParkHeapmap &&
              mountainReceiveSensitivityLayerList &&
              mountainReceiveSensitivityLayerList.map(
                (propertiesList, index) => (
                  <HeatmapLayer
                    points={propertiesList}
                    key={index}
                    latitudeExtractor={
                      pointLayerProps.latitudeExtractorForHeatmap
                    }
                    longitudeExtractor={
                      pointLayerProps.longitudeExtractorForHeatmap
                    }
                    intensityExtractor={
                      pointLayerProps.intensityExtractorForHeatmap
                    }
                  />
                )
              )}

            {!isHideFilteredPointsLayer &&
              !isHeapmapByPoint &&
              !isClusteringPoint &&
              markersByPointsList}

            {!isHideFilteredPointsLayer &&
              !isHeapmapByPoint &&
              isClusteringPoint &&
              zip(markersByPointsList, filteredPointsResponseList).map(
                ([markers, points], layerGroupIndex) => (
                  <MarkerClusterGroup
                    key={layerGroupIndex}
                    showCoverageOnHover={true}
                    spiderfyOnMaxZoom={false}
                    zoomToBoundsOnClick={false}
                    onClusterClick={(e) => {
                      const cluster = e.layer;
                      const latlngList = cluster
                        .getAllChildMarkers()
                        .map((marker) => {
                          const latlng = marker.getLatLng();
                          return `${latlng.lat},${latlng.lng}`;
                        });

                      const pointsAtSamePosition = filteredPointsResponseList
                        .map((pointsList) => {
                          return pointsList.filter(
                            ({ position: [pointLng, pointLat] }) => {
                              const latlng = `${pointLat},${pointLng}`;
                              return latlngList.includes(latlng);
                            }
                          );
                        })
                        .flat();
                      setClickedPointIconObjects(pointsAtSamePosition);
                    }}
                  >
                    {markers}
                  </MarkerClusterGroup>
                )
              )}
          </>
        )}

        {clickedPointIconObjects.length > 0 &&
          clickedPointIconObjects
            // 같은 위도/경도의 중복을 걸러냅니다. 같은 Point에 대해 Marker를 여러번 그릴 필요가 없기 때문입니다.
            .filter((feature1, index, self) => {
              const feature1Position = `${feature1.position[1]},${feature1.position[0]}`;
              return !self.slice(index + 1).find((feature2) => {
                const feature2Position = `${feature2.position[1]},${feature2.position[0]}`;
                return feature1Position === feature2Position;
              });
            })
            .map((feature, markerIndex) => {
              const {
                position: [longitude, latitude],
              } = feature;
              const iconCode = pointLayerProps.getIconCode(feature, zoom);
              if (!iconCode) {
                console.error("not found iconCode:", feature);
                return null;
              }
              const iconWidth = Math.floor(
                pointLayerProps.getIconWidth(feature, zoom) * 1.5
              );
              const { data, width, height, anchorX, anchorY } =
                iconDict[iconCode];
              const ratio = iconWidth / width;
              const iconSize = [iconWidth, Math.floor(height * ratio)];
              const iconAnchor = [anchorX * ratio, anchorY * ratio];
              const icon = L.icon({
                iconUrl: data,
                iconSize,
                iconAnchor,
              });
              return (
                <Marker
                  keyboard={false}
                  position={[latitude, longitude]}
                  icon={icon}
                  onClick={() => setClickedPointIconObjects([])}
                  zIndexOffset={10}
                  key={markerIndex}
                />
              );
            })}

        {Object.entries(mountainExtraLayerDict).map(
          ([parkTable, { icon, positionList }]) =>
            icon &&
            positionList && (
              <LayerGroup key={parkTable}>
                {positionList.map((position, index) => (
                  <Marker
                    keyboard={false}
                    position={position}
                    icon={icon}
                    key={index}
                  />
                ))}
              </LayerGroup>
            )
        )}

        {useMemo(() => {
          if (!isNationalParkHeapmap) {
            return mountainReceiveSensitivityLayerList.map(
              (propertiesList, index) => (
                <LayerGroup key={index}>
                  {propertiesList.map((feature, markerIndex) => {
                    const { position, iconCode, popupMessage } = feature;
                    const iconWidth = pointLayerProps.getIconWidth(
                      feature,
                      zoom
                    );
                    const { data, width, height, anchorX, anchorY } =
                      iconDict[iconCode];
                    const ratio = iconWidth / width;
                    const iconSize = [
                      iconWidth * 1.5,
                      Math.floor(height * ratio) * 1.5,
                    ];
                    const iconAnchor = [anchorX * ratio, anchorY * ratio];
                    const icon = L.icon({
                      iconUrl: data,
                      iconSize,
                      iconAnchor,
                    });
                    return (
                      // 산악지역 수신감도 마커
                      <Marker
                        keyboard={false}
                        key={markerIndex}
                        position={position}
                        icon={icon}
                        onMouseOver={(e) => e.target.openPopup()}
                        onMouseOut={(e) => e.target.closePopup()}
                      >
                        <Tooltip>{popupMessage}</Tooltip>
                      </Marker>
                    );
                  })}
                </LayerGroup>
              )
            );
          }
        }, [mountainReceiveSensitivityLayerList, isNationalParkHeapmap])}

        {visibleRadius && (
          <CircleMarker
            center={[circleCenter.latitude, circleCenter.longitude]}
            radius={visibleRadius / metersPerPixel}
            fillColor={"white"}
            color={"#ccc"}
            weight={1}
            opacity={1}
            fillOpacity={0.5}
          />
        )}

        {isEnableDistanceToClickedPoint &&
          clickedPointIconObjects.length > 0 &&
          (() => {
            const { position } = clickedPointIconObjects[0];
            return (
              <Polyline
                color="red"
                positions={[
                  [latitude, longitude],
                  [position[1], position[0]],
                ]}
              />
            );
          })()}

        {children}
      </LeafletMap>
    </div>
  );
};

const DEFAULT_STYLE = {
  position: "absolute",
  left: 0,
  top: 0,
  zIndex: 0,
  width: "100%",
  height: "100vh",
};

export default MapBaseLeaflet;
